import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import { Badge } from '@/components/atoms/Badge';
import { SvgIcon, type SvgIconProps } from '@/components/atoms/SvgIcon/SvgIcon';
import { useRovingTabs } from '@/hooks/useRovingTabs';

import styles from './Tabs.module.scss';

export interface TabItem<V extends string> {
    value: V;
    label: ReactNode;
    /** Makes the tab a router link; the caller then derives `value` from the location. */
    to?: string;
    icon?: SvgIconProps['icon'];
    iconSize?: SvgIconProps['size'];
    /** Shown as a count badge when above zero. */
    badgeCount?: number;
    disabled?: boolean;
}

export interface TabsProps<V extends string> {
    items: ReadonlyArray<TabItem<V>>;
    value: V | undefined;
    /** Called with the tab's own click event, also when the arrow keys select it. */
    onChange?: (value: V, event: MouseEvent<HTMLElement>) => void;
    /** `equal` gives every tab the same width. */
    layout?: 'default' | 'equal';
    'aria-label'?: string;
    className?: string;
}

/**
 * Underlined tabs above a page section or a panel. Use SegmentedControl instead for a
 * compact switch inside a card or a toolbar.
 */
export function Tabs<V extends string>(props: TabsProps<V>) {
    const { items, value, onChange, layout = 'default', className } = props;
    const selectedIndex = items.findIndex((item) => item.value === value);

    const roving = useRovingTabs({
        count: items.length,
        selectedIndex,
        isDisabled: (index) => items[index]?.disabled === true,
        activatesOnFocus: (index) => items[index]?.to === undefined,
    });

    const select = (item: TabItem<V>, event: MouseEvent<HTMLElement>) => {
        if (item.disabled) {
            event.preventDefault();
            return;
        }
        onChange?.(item.value, event);
    };

    return (
        <div className={classNames(styles.root, className)}>
            <div
                role="tablist"
                aria-label={props['aria-label']}
                className={styles.nav}
                data-layout={layout}>
                {items.map((item, index) => {
                    const isSelected = item.value === value;
                    const handleKeyDown = (event: KeyboardEvent<HTMLElement>) =>
                        roving.onKeyDown(event, index);
                    const content = (
                        <>
                            {item.icon ? (
                                <SvgIcon
                                    icon={item.icon}
                                    size={item.iconSize ?? 'medium'}
                                    color="currentColor"
                                />
                            ) : null}
                            <span className={styles.label}>{item.label}</span>
                            {typeof item.badgeCount === 'number' && item.badgeCount > 0 ? (
                                <Badge tone="accent" variant="solid">
                                    {item.badgeCount}
                                </Badge>
                            ) : null}
                        </>
                    );

                    return item.to !== undefined ? (
                        <Link
                            key={item.value}
                            ref={roving.setRef(index)}
                            role="tab"
                            aria-selected={isSelected}
                            aria-disabled={item.disabled || undefined}
                            tabIndex={roving.tabIndexFor(index)}
                            className={styles.tab}
                            to={item.to}
                            onKeyDown={handleKeyDown}
                            onClick={(event) => select(item, event)}>
                            {content}
                        </Link>
                    ) : (
                        <button
                            key={item.value}
                            ref={roving.setRef(index)}
                            type="button"
                            role="tab"
                            aria-selected={isSelected}
                            tabIndex={roving.tabIndexFor(index)}
                            className={styles.tab}
                            disabled={item.disabled}
                            onKeyDown={handleKeyDown}
                            onClick={(event) => select(item, event)}>
                            {content}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

export default Tabs;
